// ─── What: shared y-axis fit setting ──────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import type { AxisFit } from '../components/charts/chartYDomain';

export const AXIS_FIT_KEY = 'irm.chart.axisFit';

/** Every mounted chart reads one value, so the toggle applies app-wide. */
const listeners = new Set<(fit: AxisFit) => void>();
let current: AxisFit | null = null;

function read(): AxisFit {
    if (current) return current;
    const stored = localStorage.getItem(AXIS_FIT_KEY);
    current = stored === 'full' || stored === 'auto' ? stored : 'auto';
    return current;
}

/** Test seam: drop the cached value so the next read hits localStorage. */
export function resetAxisFitCache(): void {
    current = null;
}

// ─── How it works ─────────────────────────────────────────────────────────────

/** Read the shared auto/full axis setting and flip it for every chart at once. */
export function useAxisFit(): { fit: AxisFit; toggleFit: () => void } {
    const [fit, setFit] = useState<AxisFit>(read);

    useEffect(() => {
        listeners.add(setFit);
        setFit(read());
        return () => {
            listeners.delete(setFit);
        };
    }, []);

    const toggleFit = useCallback(() => {
        current = read() === 'auto' ? 'full' : 'auto';
        localStorage.setItem(AXIS_FIT_KEY, current);
        for (const l of listeners) l(current);
    }, []);

    return { fit, toggleFit };
}
